import React, { useState, useEffect, useRef } from 'react';
import { default as ReactSelect } from 'react-select';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';

const ConditionalSelect = ({ isAsync, ...allProps }) => {
  if (isAsync) {
    return <AsyncSelect {...allProps} />;
  } else {
    return <ReactSelect {...allProps} />;
  }
};

/**
 * @deprecated Use Telerik or shared components instead.
 */
const Select = ({
  isAbsolute = false,
  isCapacity = false,
  isTableSelect = false,
  isWarehouse = false,
  menuHeight,
  disabled,
  isAsync = false,
  hideDropdownIndicator = false,
  overflowModal = false,
  ...selectProps
}) => {
  const [allSelected, setAllSelected] = useState(false);

  const { value, options, allOption, onChange } = selectProps;

  if ((typeof value === 'string' || typeof value === 'number') && options.length) {
    const filteredOptions = options.filter(x => x.value === value);
    if (filteredOptions.length === 1) {
      selectProps.value = {
        label: filteredOptions[0].label,
        value: filteredOptions[0].value
      };
    }
  }

  let containerStyle = { margin: 0 };
  if (isWarehouse) {
    containerStyle = { ...containerStyle, position: 'unset', width: '100%', marginTop: '.25rem' };
  } else if (isAbsolute && !isCapacity) {
    containerStyle = { ...containerStyle, position: 'absolute', width: '100%', marginTop: '.25rem' };
  } else if (isCapacity) {
    containerStyle = { ...containerStyle, position: 'absolute', width: 'inherit' };
  }

  let styles = {
    container: styles => ({ ...styles, ...containerStyle }),
    control: styles => ({
      ...styles,
      borderRadius: '5px',
      borderColor: '#D3D3D3',
      borderWidth: '1px',
      margin: 0
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    indicatorsContainer: styles => ({
      ...styles,
      margin: 0,
      padding: '0.5rem'
    }),
    menu: styles => ({
      ...styles,
      zIndex: 11, // resizer on tables is at 10, this must be higher
      maxHeight: menuHeight ? menuHeight : styles.maxHeight
    }),
    menuList: styles => ({
      ...styles,
      maxHeight: menuHeight ? menuHeight : styles.maxHeight
    }),
    dropdownIndicator: styles => ({
      ...styles,
      margin: 0,
      padding: 0,
      display: hideDropdownIndicator ? 'none' : styles.display
    }),
    clearIndicator: styles => ({
      ...styles,
      margin: 0,
      padding: 0
    }),
    option: (styles, props) => {
      return {
        ...styles,
        margin: 0,
        color: !props.isFocused && !props.isSelected ? '#484848' : '#fff',
        ...(props.isDisabled ? { opacity: '0.5' } : {})
      };
    },
    multiValue: styles => {
      return {
        ...styles,
        color: '#FFFFFF',
        backgroundColor: '#3384ae',
        marginRight: '5px !important' // overrides CSS value for all selects -> has to be important
      };
    },
    multiValueLabel: styles => ({
      ...styles,
      color: '#FFFFFF'
    }),
    multiValueRemove: styles => ({
      ...styles,
      ':hover': {
        backgroundColor: '#00517b'
      }
    }),
    placeholder: styles => ({ ...styles, color: '#bbbbbb' }),
    valueContainer: styles => ({ ...styles, margin: 0 }),
    loadingIndicator: styles => ({ ...styles, marginBottom: 0 }),
    loadingMessage: styles => ({ ...styles, marginBottom: 0 })
  };

  const Option = props => {
    return (
      <components.Option {...props}>
        <div className='multi-select-option'>
          <div className='checkbox-container'>
            <input type='checkbox' checked={props.value === '*' ? allSelected : props.isSelected} onChange={() => null} />{' '}
          </div>
          <div>
            <label>{props.label}</label>
          </div>
        </div>
      </components.Option>
    );
  };

  const selectAllOption = {
    label: 'Select all',
    value: '*'
  };

  const ValueContainer = ({ children, ...props }) => {
    var valuesComponent;
    if (props.selectProps.value && props.selectProps.value.length) {
      if (props.selectProps.value.length === 1) {
        valuesComponent = <div>{props.selectProps.value[0].label ? props.selectProps.value[0].label : '1 Item Selected'}</div>;
      } else if (props.selectProps.useCommaSeparatedValues && props.selectProps.value.every(v => v.label)) {
        valuesComponent = <div>{props.selectProps.value.map(v => v.label).join(', ')}</div>;
      } else {
        valuesComponent = <div>{`${props.selectProps.value.length} Items Selected`}</div>;
      }
    } else {
      valuesComponent = React.Children.map(children, child => (child && [components.SingleValue].indexOf(child.type) === -1 ? child : null));
    }
    return <components.SingleValue {...props}>{valuesComponent}</components.SingleValue>;
  };

  if (overflowModal) {
    selectProps.menuPortalTarget = document.body;
    styles.menuPortal = base => ({ ...base, zIndex: 99999 });
  }

  return selectProps.isMulti ? (
    <ConditionalSelect
      isAsync={isAsync}
      {...selectProps}
      styles={styles}
      theme={theme => {
        return {
          ...theme,
          colors: {
            ...theme.colors,
            primary: '#3384AE',
            primary25: '#3384AE',
            primary50: '#3384AE',
            danger: '#e60000'
          }
        };
      }}
      components={{ Option, MultiValue: ValueContainer }}
      isDisabled={disabled}
      className='select'
      classNamePrefix='gwi-select'
      options={[selectAllOption, ...options]}
      onChange={selected => {
        if (selected.length === 0) {
          setAllSelected(false);
        }
        if (selected !== null && selected.length > 0 && selected[selected.length - 1].value === selectAllOption.value) {
          if (allSelected) {
            setAllSelected(false);
            return onChange([]);
          } else {
            setAllSelected(true);
            return onChange(options);
          }
        }
        return onChange(selected);
      }}
    />
  ) : (
    <ConditionalSelect
      isAsync={isAsync}
      isDisabled={disabled}
      className='select'
      classNamePrefix={`gwi-select${isTableSelect ? ' tableSelect' : ''}`}
      theme={theme => {
        return {
          ...theme,
          colors: {
            ...theme.colors,
            primary: '#3384AE',
            primary25: '#3384AE',
            primary50: '#3384AE',
            danger: '#e60000'
          }
        };
      }}
      styles={styles}
      {...selectProps}
    />
  );
};

export default Select;
